import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { MessageSquare, Send, Mail, Trash2, ChevronDown, ChevronUp, Edit2, Check, X, MoreVertical } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { notesAPI } from '../lib/documentsAPI';
import api from '../lib/api';
import { formatRelativeTime } from '../lib/fileUtils';
import { ClientPortalMessages } from '../components/comms/ClientPortalMessages';

// Release 1 (Option A): client communications = EMAILS ONLY. The notes-backed "Chats"
// section is not shipped yet (internal notes aren't working), so it's hidden behind this
// flag — flip to true to bring it back once notes are fixed.
const SHOW_CHATS = false;

export default function ClientCommunications() {
  const [clientId, setClientId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [emails, setEmails] = useState([]);
  const [notes, setNotes] = useState([]);
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [newMessageBody, setNewMessageBody] = useState('');
  const [currentUserId, setCurrentUserId] = useState(null);
  const messagesEndRef = useRef(null);

  // Email Dialog States
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailRecipient, setEmailRecipient] = useState('');
  const [emailCc, setEmailCc] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);


  // Expand/Collapse states
  const [notesExpanded, setNotesExpanded] = useState(true);
  const [emailsExpanded, setEmailsExpanded] = useState(true);

  // Email detail view state
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailDetailOpen, setEmailDetailOpen] = useState(false);

  // Edit note state
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteBody, setEditingNoteBody] = useState('');

  useEffect(() => {
    loadClientData();
  }, []);

  useEffect(() => {
    if (clientId) {
      loadCommunications();
      const interval = setInterval(loadCommunications, 30000);
      return () => clearInterval(interval);
    }
  }, [clientId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadClientData = async () => {
    try {
      const response = await api.get('/api/me');
      const cid = response.data.active_membership?.client_id;
      const userId = response.data.user.id;

      if (!cid) {
        toast.error('Your account is not linked to a client profile');
        setLoading(false);
        return;
      }

      setClientId(cid);
      setCurrentUserId(userId);

      const clientResponse = await api.get(`/api/clients/${cid}`);
      setClientName(clientResponse.data.display_name);
      setClientEmail(clientResponse.data.email || '');
    } catch (error) {
      toast.error('Failed to load your profile');
      setLoading(false);
    }
  };

  const loadCommunications = async () => {
    if (!clientId) return;

    try {
      // Load notes (visible to client)
      const notesResponse = await notesAPI.list(clientId);
      const visibleNotes = notesResponse.data.filter(msg => !msg.is_staff_only);
      setNotes(visibleNotes);

      // Load emails
      try {
        const emailsResponse = await api.get(`/api/emails?client_id=${clientId}`);
        setEmails(emailsResponse.data || []);
      } catch (err) {
        console.log('Emails not loaded');
      }
    } catch (error) {
      console.error('Failed to load communications');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessageBody.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSubmitting(true);
    try {
      await notesAPI.create(clientId, {
        body: newMessageBody,
        is_staff_only: false
      });

      toast.success('Message sent');
      setNewMessageBody('');
      loadCommunications();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailSubject.trim() || !emailBody.trim()) {
      toast.error('Please enter a subject and message');
      return;
    }

    setSendingEmail(true);
    try {
      // Recipient (your firm) is resolved server-side — to:'' = auto-route.
      await api.post('/api/emails', {
        client_id: clientId,
        to: '',
        cc: emailCc,
        subject: emailSubject,
        body: emailBody
      });

      toast.success('Email sent successfully');
      setEmailDialogOpen(false);
      setEmailSubject('');
      setEmailBody('');
      setEmailRecipient('');
      setEmailCc('');
      loadCommunications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleOpenEmailDialog = () => {
    setEmailRecipient(clientEmail);
    setEmailDialogOpen(true);
  };

  const handleViewEmail = (email) => {
    setSelectedEmail(email);
    setEmailDetailOpen(true);
  };

  const handleStartEditNote = (note) => {
    setEditingNoteId(note.id);
    setEditingNoteBody(note.body);
  };

  const handleSaveEditNote = async (noteId) => {
    if (!editingNoteBody.trim()) {
      toast.error('Note cannot be empty');
      return;
    }

    try {
      await api.patch(`/api/notes/${noteId}`, { body: editingNoteBody });
      toast.success('Note updated');
      setEditingNoteId(null);
      setEditingNoteBody('');
      loadCommunications();
    } catch (error) {
      toast.error('Failed to update note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!confirm('Are you sure you want to delete this note?')) return;

    try {
      await api.delete(`/api/notes/${noteId}`);
      toast.success('Note deleted');
      loadCommunications();
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const canEditNote = (note) => {
    if (note.created_by !== currentUserId) return false;
    const messageTime = new Date(note.created_at);
    const now = new Date();
    const diffMinutes = (now - messageTime) / (1000 * 60);
    return diffMinutes <= 5;
  };

  const canDeleteNote = (note) => {
    return note.created_by === currentUserId;
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="h-96 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  if (!clientId) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-12 text-center">
            <MessageSquare className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="text-lg font-semibold text-foreground mb-2">Unable to Access Communications</h3>
            <p className="text-muted-foreground">Your account is not linked to a client profile.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>
            Messages
          </h1>
          <p className="text-muted-foreground mt-1">Message your accountant and view your email history</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleOpenEmailDialog} className="bg-blue-600 hover:bg-blue-700">
            <Mail className="w-4 h-4 mr-2" />
            Send Email
          </Button>
        </div>
      </div>

      {/* Portal conversations (Communications Hub) */}
      <ClientPortalMessages />

      {/* Emails Section */}
      <Card>
        <CardHeader
          className="cursor-pointer hover:bg-muted/50"
          onClick={() => setEmailsExpanded(!emailsExpanded)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              <CardTitle className="text-lg">Emails</CardTitle>
              <Badge className="bg-blue-100 text-blue-800">{emails.length}</Badge>
            </div>
            {emailsExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>
        </CardHeader>
        {emailsExpanded && (
          <CardContent>
            {emails.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No emails yet</p>
            ) : (
              <div className="space-y-3">
                {emails.map((email) => (
                  <div
                    key={email.id}
                    className="p-4 border rounded-lg hover:bg-muted/50 cursor-pointer"
                    onClick={() => handleViewEmail(email)}
                  >
                    <div className="flex items-start gap-3">
                      <Avatar className="w-10 h-10">
                        <AvatarFallback className="bg-blue-600 text-white">
                          {getInitials(email.from_name || 'Admin')}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-semibold text-sm">{email.from_name || 'Admin User'}</p>
                          <p className="text-xs text-muted-foreground">{formatRelativeTime(email.sent_at || email.created_at)}</p>
                        </div>
                        <p className="font-medium text-sm mb-1">Subject: {email.subject}</p>
                        <p className="text-sm text-muted-foreground">To: {email.to}</p>
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{email.body}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Chats Section — hidden in release 1 (notes feature not shipped yet) */}
      {SHOW_CHATS && (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-primary" />
              <CardTitle className="text-lg">Chats</CardTitle>
              <Badge className="bg-accent text-accent-foreground">{notes.length}</Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Chat Messages */}
          <div className="max-h-96 overflow-y-auto space-y-3">
            {notes.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No chats yet</p>
            ) : (
              notes.map((note) => (
                <div key={note.id} className="p-4 bg-muted/50 border border-border rounded-lg">
                  <div className="flex items-start gap-3">
                    <Avatar className="w-10 h-10">
                      <AvatarFallback className="bg-primary text-white">
                        {getInitials(note.author_name || 'User')}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-sm">{note.author_name || 'User'}</p>
                          <p className="text-xs text-muted-foreground">{formatRelativeTime(note.created_at)}</p>
                        </div>
                        {(canEditNote(note) || canDeleteNote(note)) && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted"
                              >
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {canEditNote(note) && editingNoteId !== note.id && (
                                <DropdownMenuItem onClick={() => handleStartEditNote(note)}>
                                  <Edit2 className="w-4 h-4 mr-2" />
                                  Edit Message
                                </DropdownMenuItem>
                              )}
                              {canDeleteNote(note) && (
                                <>
                                  {canEditNote(note) && <DropdownMenuSeparator />}
                                  <DropdownMenuItem
                                    onClick={() => handleDeleteNote(note.id)}
                                    className="text-red-600"
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete Message
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                      {editingNoteId === note.id ? (
                        <div className="space-y-2">
                          <Textarea
                            value={editingNoteBody}
                            onChange={(e) => setEditingNoteBody(e.target.value)}
                            rows={3}
                          />
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => handleSaveEditNote(note.id)}>
                              <Check className="w-3 h-3 mr-1" /> Save
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingNoteId(null)}>
                              <X className="w-3 h-3 mr-1" /> Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm whitespace-pre-wrap">{note.body}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Message Input Area */}
          <div className="border-t pt-4">
            <div className="space-y-3">
              <Textarea
                placeholder="Type your message... (Press Enter to send, Shift+Enter for new line)"
                value={newMessageBody}
                onChange={(e) => setNewMessageBody(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                rows={3}
                className="resize-none"
              />
              <div className="flex justify-end">
                <Button
                  onClick={handleSendMessage}
                  disabled={submitting || !newMessageBody.trim()}
                  className="bg-primary hover:bg-primary/90"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {submitting ? 'Sending...' : 'Send Message'}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Email Send Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Send Email</DialogTitle>
            <DialogDescription>Send an email to your accountant</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Recipient is fixed — emails go to your accountant / firm automatically. */}
            <div className="space-y-1">
              <Label>To</Label>
              <div className="flex items-center gap-2 rounded-md border border-border bg-muted/50 px-3 py-2 text-sm text-foreground/90">
                <Mail className="w-4 h-4 text-muted-foreground/70" />
                <span className="font-medium">Your accountant</span>
              </div>
              <p className="text-xs text-muted-foreground">Sent to your firm. Use Cc to copy others.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email-cc">Cc <span className="text-muted-foreground/70 font-normal">(optional)</span></Label>
              <Input
                id="email-cc"
                type="text"
                placeholder="person@example.com, another@example.com"
                value={emailCc}
                onChange={(e) => setEmailCc(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email-subject">Subject</Label>
              <Input
                id="email-subject"
                placeholder="Enter email subject"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email-body">Message</Label>
              <Textarea
                id="email-body"
                placeholder="Type your email message here..."
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                rows={10}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSendEmail}
              disabled={sendingEmail}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Send className="w-4 h-4 mr-2" />
              {sendingEmail ? 'Sending...' : 'Send Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Detail Dialog */}
      <Dialog open={emailDetailOpen} onOpenChange={setEmailDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Email Details</DialogTitle>
          </DialogHeader>
          {selectedEmail && (
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-muted-foreground">From:</Label>
                <p className="font-medium">{selectedEmail.from_name || 'Admin User'}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">To:</Label>
                <p className="font-medium">{selectedEmail.to}</p>
              </div>
              {selectedEmail.cc && (
                <div>
                  <Label className="text-muted-foreground">Cc:</Label>
                  <p className="font-medium">{selectedEmail.cc}</p>
                </div>
              )}
              <div>
                <Label className="text-muted-foreground">Subject:</Label>
                <p className="font-medium">{selectedEmail.subject}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Date:</Label>
                <p className="font-medium">{formatRelativeTime(selectedEmail.sent_at || selectedEmail.created_at)}</p>
              </div>
              <div className="border-t pt-4">
                <Label className="text-muted-foreground">Message:</Label>
                <div className="mt-2 whitespace-pre-wrap text-sm">{selectedEmail.body}</div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDetailOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
